import { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, Loader2 } from "lucide-react";
import { api } from "@/lib/api";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "polling", data: null, error: null });
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState({ status: "error", data: null, error: "Missing session id." });
      return;
    }
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState({ status: "paid", data, error: null });
          return;
        }
        attempts.current += 1;
        if (attempts.current > 20) {
          setState({ status: "timeout", data, error: null });
          return;
        }
        setTimeout(poll, 2000);
      } catch (e) {
        setState({ status: "error", data: null, error: "Could not verify payment." });
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <main data-testid="page-payment-success" className="min-h-screen bg-[#050505] px-6 pt-32 pb-24 flex flex-col items-center text-center">
      {state.status === "polling" && (
        <>
          <Loader2 className="animate-spin text-[#7C3AED]" size={40} />
          <p className="mt-6 text-white/60">Confirming your payment…</p>
        </>
      )}
      {state.status === "paid" && (
        <>
          <CheckCircle2 className="text-green-400" size={56} />
          <h1 className="mt-6 font-display text-5xl md:text-6xl uppercase tracking-tight text-white">Thank you.</h1>
          <p className="mt-4 max-w-md text-white/70">
            Your order for <span className="text-white">{state.data?.title}</span> is confirmed
            {state.data?.amount != null && ` ($${(state.data.amount/100).toFixed(2)})`}.
            A receipt has been emailed to you. We&apos;ll ship to the address you provided at checkout.
          </p>
          <Link to="/" className="magnetic-btn mt-10 rounded-full bg-[#7C3AED] px-8 py-3 text-sm font-semibold uppercase tracking-wider text-white hover:bg-white hover:text-black">
            Back to Home
          </Link>
        </>
      )}
      {(state.status === "timeout" || state.status === "error") && (
        <>
          <h1 className="font-display text-4xl uppercase text-white">Payment received</h1>
          <p className="mt-4 max-w-md text-white/70">We&apos;re still confirming your payment. If your card was charged, you&apos;ll get an email shortly.</p>
          <Link to="/" className="magnetic-btn mt-10 rounded-full border border-white/30 px-8 py-3 text-sm uppercase tracking-wider text-white hover:bg-white hover:text-black">
            Back to Home
          </Link>
        </>
      )}
    </main>
  );
}
